import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { MemberPaths } from "src/app/constants/routes";

import { EnrollmentOnboardService } from 'src/app/services/enrollment-onboard.service';
import { BenefitOnboardWizardPanel } from "src/app/constants/wizard-panels";
import { ToastService } from "src/app/services/toast.service";
import { ToastType } from "src/app/constants/toast-type";
import { NextPanel } from "src/app/models/next-panel";
import { EnrollmentFinalizeResponse, EnrolmentOnboardModel, OnboardingEnrollmentsItem, OnboardingModeSelection } from "src/app/models/enrollment-onboard";
import { EnrollmentManagerService } from "src/app/services/enrollment-manager.service";
import { ContentHeaderKey } from "src/app/constants/content-header";
import { Member } from "src/app/models/member";
import { SvgMatType } from "src/app/constants/icons";
import { NavigationService } from "src/app/services/navigation.service";
import { InPageNavItem } from "src/app/models/in-page-nav";
import { MemberService } from "src/app/services/member.service";

@Component({
    selector: 'mwp-benefit-enrollment-onboarding-wizard',
    templateUrl: './onboarding-wizard.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EnrollmentOnboardingComponent implements OnInit {
    public apiUrl = ContentHeaderKey.EnrollmentBenefitsDashboard;
    public currentPanel: BenefitOnboardWizardPanel;
    public enrolmentOnboardModel: EnrolmentOnboardModel;
    public enrollmentFinalizeResponse: EnrollmentFinalizeResponse;
    public displayModePanel: boolean;
    public member: Member;
    public svgType = SvgMatType;
    public MemberPaths = MemberPaths;
    public navItems: InPageNavItem[];

    constructor(
        private enrollmentOnboardService: EnrollmentOnboardService,
        public enrollmentManagerService: EnrollmentManagerService,
        private toastService: ToastService,
        private router: Router,
        private navService: NavigationService,
        private memberService: MemberService,
    ) {
        this.navItems = this.navService.buildMemberPageNavItems(this.router.url);
    }

    ngOnInit(): void {
        if (this.enrollmentManagerService.manager && this.enrollmentManagerService.manager.enrollmentMode) {
            this.displayModePanel = true;
        } else {
            this.currentPanel = BenefitOnboardWizardPanel.initialize;
            this.setPanel(this.currentPanel, null);
        }

        this.memberService.getMemberProfile().subscribe((response: Member) => {
            this.member = response;
        });
    }

    private resetPanels(): void {
        this.displayModePanel = false;

        this.enrolmentOnboardModel =
            this.enrollmentFinalizeResponse = null;
    }

    private setPanel(panelName: string, payload: any): void {
        switch (panelName) {
            case BenefitOnboardWizardPanel.initialize:
                this.enrollmentOnboardService.getInitialize().subscribe((response: NextPanel) => {
                    this.getPanel(response.nextPanel.value); 
                });
                break;

            case BenefitOnboardWizardPanel.modeSelection:
                const l_OnboardingEnrollmentsItem: OnboardingEnrollmentsItem = payload as OnboardingEnrollmentsItem;
                const l_OnboardingModeSelection: OnboardingModeSelection = {
                    mode: l_OnboardingEnrollmentsItem.mode,
                };
                
                this.enrollmentOnboardService.autoOnboarding(l_OnboardingModeSelection).subscribe((response: NextPanel) => {
                    this.enrollmentManagerService.setEnrollmentMode(l_OnboardingEnrollmentsItem);
                    this.navigateToEnrollment();
                });
                break;

            case BenefitOnboardWizardPanel.profile:
            case BenefitOnboardWizardPanel.modeSelection:
            case BenefitOnboardWizardPanel.enrollmentIntro:
            case BenefitOnboardWizardPanel.skip:
            case BenefitOnboardWizardPanel.qualifyingEvent:
            case BenefitOnboardWizardPanel.requestReview:
            default:
                this.toastService.buildToastMessage(ToastType.error, '', `Panel ${panelName} is not configured. Please notify Administrator of this error and take a screen shot.`);
                break;
        }
    }

    private getPanel(panelName: string): void {
        switch (panelName) {
            case BenefitOnboardWizardPanel.modeSelection:
                this.currentPanel = BenefitOnboardWizardPanel.modeSelection;
                this.enrollmentOnboardService.getMode().subscribe((response: EnrolmentOnboardModel) => {
                    this.resetPanels();
                    this.enrolmentOnboardModel = response;
                    this.displayModePanel = true;
                });
                break;

            case BenefitOnboardWizardPanel.profile:
            case BenefitOnboardWizardPanel.enrollmentIntro:
            case BenefitOnboardWizardPanel.initialize:
            case BenefitOnboardWizardPanel.skip:
            case BenefitOnboardWizardPanel.qualifyingEvent:
            case BenefitOnboardWizardPanel.requestReview:
            default:
                this.toastService.buildToastMessage(ToastType.error, '', `Panel ${panelName} is not configured functionally. Please notify Administrator of this error and take a screen shot.`);
                break;
        }
    }

    public processPanel(data: any): void {
        this.setPanel(this.currentPanel, data);
    }

    public continueSession(): void {
        this.enrollmentManagerService.resetBehaviorSubjects();
        this.navigateToEnrollment();
    }

    private navigateToEnrollment(): void {
        this.router.navigateByUrl(MemberPaths.BenefitEnrollment);
    }
}
